import { useEffect, useRef, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { useMaxwellRunDetail } from "@/hooks/useMaxwellRuns";
import { humanizeState } from "@/lib/text";
import type { RunStepProgress } from "@/types/maxwell";

import { StepActionBar } from "./activity/StepActionBar";
import { RunStatusBadge } from "./activity/RunStatusBadge";

/** Run statuses the runner will never advance past — polling is over. */
const TERMINAL_STATUSES = new Set(["done", "failed", "cancelled"]);

/**
 * Inline checkpoint card for a background agent run started from this chat
 * turn. Polls the run (`useMaxwellRunDetail`, already refetches on
 * `pending`/`running`) and renders whichever step is actionable — same
 * `StepActionBar` + inbox notification path as Activity and the Inbox tab, so
 * approving here resolves the same notification everywhere else it's shown.
 * When the run reaches a terminal status, the thread queries are invalidated
 * so the answer message the runner posted into this conversation appears
 * without leaving chat: a completed run's card yields to that answer, and a
 * failed run's card shows the actual failure reason. A fetch error (after the
 * query's single retry) surfaces as a status line rather than silently
 * rendering nothing forever.
 */
export function RunCheckpointCard({
  runId,
  progress,
  onPromote,
}: {
  runId: string;
  /** Live per-step frames from the in-chat drive stream, when this surface is
   *  driving the run synchronously. */
  progress?: RunStepProgress[];
  /** Hand the run to the background worker ("Move to background"). */
  onPromote?: (runId: string) => Promise<void>;
}) {
  const { data, isError } = useMaxwellRunDetail(runId);
  const qc = useQueryClient();
  const [promoting, setPromoting] = useState(false);
  const [promoted, setPromoted] = useState(false);
  const status = data?.run.status;

  // On the transition into a terminal status, refetch the persisted thread —
  // the runner posts the run's answer into this conversation on completion.
  const refreshedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!status || !TERMINAL_STATUSES.has(status)) return;
    if (refreshedFor.current === runId) return;
    refreshedFor.current = runId;
    void qc.invalidateQueries({ queryKey: ["maxwell", "thread"] });
  }, [status, runId, qc]);

  if (!data && !isError) return null;

  if (!data) {
    return (
      <p className="max-w-[85%] self-start text-xs text-muted-foreground">
        Couldn't load run status.
      </p>
    );
  }

  const open = data.steps.find((s) => s.needs_you && s.approval_notification_id);
  if (open) {
    return (
      <div className="max-w-[85%] self-start rounded-md border border-l-[3px] border-l-warning bg-card p-3">
        <p className="text-sm font-medium text-foreground">
          Needs you: {humanizeState(open.capability)}
        </p>
        <div className="mt-2">
          <StepActionBar
            notificationId={open.approval_notification_id!}
            isHitl={open.status === "awaiting_human"}
          />
        </div>
      </div>
    );
  }

  // Complete: the runner posted the run's answer into this conversation and
  // the invalidation above pulls it into the transcript — the card yields to
  // the real answer instead of a "Run complete." status line.
  if (data.run.status === "done") return null;

  if (data.run.status === "failed") {
    return (
      <p className="max-w-[85%] self-start text-xs text-destructive">
        Run failed{data.run.last_error ? `: ${data.run.last_error}` : "."}
      </p>
    );
  }

  if (data.run.status === "cancelled") {
    return <p className="max-w-[85%] self-start text-xs text-muted-foreground">Run cancelled.</p>;
  }
  if (data.run.status === "paused") {
    return <p className="max-w-[85%] self-start text-xs text-muted-foreground">Run paused.</p>;
  }

  // Still running. When this surface is driving the run live, show each step
  // as it executes plus the hand-off to the background worker; otherwise the
  // compact status line.
  const steps = progress ?? [];
  if (steps.length === 0 && !onPromote) {
    return <p className="max-w-[85%] self-start text-xs text-muted-foreground">Working on it…</p>;
  }

  const promote = async () => {
    if (!onPromote) return;
    setPromoting(true);
    try {
      await onPromote(runId);
      setPromoted(true);
      toast.success("Run moved to the background");
    } catch {
      toast.error("Failed to move the run to the background");
    } finally {
      setPromoting(false);
    }
  };

  return (
    <div className="max-w-[85%] self-start rounded-md border border-border bg-card p-3">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm font-medium text-foreground">Working on it…</p>
        {onPromote && !promoted && (
          <Button size="sm" variant="outline" disabled={promoting} onClick={() => void promote()}>
            Move to background
          </Button>
        )}
      </div>
      {steps.length > 0 && (
        <ol className="mt-2 flex flex-col gap-1">
          {steps.map((s) => (
            <li key={s.step_index} className="flex items-center justify-between gap-2">
              <span className="truncate text-sm text-foreground">
                {s.capability ?? `Step ${s.step_index + 1}`}
              </span>
              <RunStatusBadge status={s.status === "committed" ? "done" : s.status} />
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
